import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getUsers,
  getAdminStats,
  getVideosReport,
  getAdminVideos,
  updateAdminVideoComment,
  getAdminVideoJobs,
  getAdminJobCurl,
  retryAdminJobUpload,
  rerenderAdminJob,
  getIntegrationsStatus,
  refreshIntegrationsStatus,
  addUserRole,
} from "@app/services/admin";
import { downloadVideosReportExcel } from "@app/services/adminReport";
import { videoKey } from "@app/hooks/videos";

export const adminKeys = {
  all: ["admin"],
  users: () => [...adminKeys.all, "users"],
  stats: (filters) => [...adminKeys.all, "stats", filters],
  videos: (filters = {}) => [...adminKeys.all, "videos", filters],
  videoJobs: (videoId) => [...adminKeys.all, "videoJobs", videoId],
  integrations: (complex = false) => [
    ...adminKeys.all,
    "integrations",
    { complex },
  ],
};

export const useAdminUsers = (options = {}) => {
  return useQuery({
    queryKey: adminKeys.users(),
    queryFn: ({ signal }) => getUsers({ signal }),
    ...options,
  });
};

export const useAdminStats = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: adminKeys.stats(filters),
    queryFn: ({ signal }) => getAdminStats(filters, { signal }),
    ...options,
  });
};

export const useAdminVideos = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: adminKeys.videos(filters),
    queryFn: ({ signal }) => getAdminVideos(filters, { signal }),
    meta: {
      errorMessage: "Failed to load videos. Please try again.",
    },
    ...options,
  });
};

export const useUpdateAdminVideoComment = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comment }, { signal }) =>
      updateAdminVideoComment(id, comment, { signal }),
    onSuccess: (updatedVideo, variables) => {
      const targetId = updatedVideo?.id ?? variables?.id;
      const nextComment = updatedVideo?.comment ?? variables?.comment ?? "";

      queryClient.setQueriesData(
        { queryKey: [...adminKeys.all, "videos"] },
        (videos) =>
          Array.isArray(videos)
            ? videos.map((video) =>
                video.id === targetId
                  ? { ...video, comment: nextComment }
                  : video,
              )
            : videos,
      );

      if (targetId) {
        queryClient.invalidateQueries({ queryKey: videoKey.detail(targetId) });
      }
    },
    meta: {
      errorMessage: "Failed to save comment. Please try again.",
    },
    ...options,
  });
};

export const useIntegrationsStatus = (complex = false, options = {}) => {
  return useQuery({
    queryKey: adminKeys.integrations(complex),
    queryFn: ({ signal }) => getIntegrationsStatus({ complex, signal }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    ...options,
  });
};

export const useRefreshIntegrationsStatus = (complex = false, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ signal } = {}) => refreshIntegrationsStatus({ complex, signal }),
    onSuccess: (data) => {
      queryClient.setQueryData(adminKeys.integrations(complex), data);
    },
    meta: {
      errorMessage: "Failed to refresh integration status. Please try again.",
    },
    ...options,
  });
};

export const useDownloadVideosReport = (options = {}) => {
  return useMutation({
    mutationFn: async (filters = {}) => {
      const reportName =
        filters.fromDate && filters.toDate
          ? `videos-${filters.fromDate}-${filters.toDate}.xlsx`
          : "videos-all.xlsx";
      const videos = await getVideosReport(filters);
      await downloadVideosReportExcel(videos, reportName);
      return videos.length;
    },
    meta: {
      errorMessage: "Failed to download report. Please try again.",
    },
    ...options,
  });
};

export const useAddUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userID, role }) => addUserRole(userID, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
};

export const useAdminVideoJobs = (videoId, options = {}) => {
  return useQuery({
    queryKey: adminKeys.videoJobs(videoId),
    queryFn: ({ signal }) => getAdminVideoJobs(videoId, { signal }),
    enabled: Boolean(videoId),
    ...options,
  });
};

export const useAdminJobCurl = (options = {}) => {
  return useMutation({
    mutationFn: ({ jobId }, { signal }) => getAdminJobCurl(jobId, { signal }),
    ...options,
  });
};

export const useAdminJobRetry = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, action }, { signal }) =>
      action === "rerender"
        ? rerenderAdminJob(jobId, { signal })
        : retryAdminJobUpload(jobId, { signal }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, "videos"] });
    },
    ...options,
  });
};
